import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import { withRouter } from "react-router-dom";
import { Spinner } from "reactstrap";
import PageDisplay from "../Base/PageDisplay/PageDisplay";

import { APP_TITLE } from "../../Config/Environment";
import { FixtureDetailsContext } from "../../Config/FixtureDetails";
import { StaffRoles } from "../../Config/StaffRoles";
import { uniqueBy } from "../Base/Utilities/CollectionUtilities";
import { getTruncatedTeamName, getValidationObject } from "../Base/Utilities/EntityUtilities";
import { sendPlayer } from "../Base/Utilities/ManagePlayers";
import { Team } from "./Team";
import TeamSetupControls from "./TeamSetupControls";

import "./TeamSetup.scss";

const buildTeamManagerSyncStorageKey = (fixtureId, entityId) => {
  return `${fixtureId}-${entityId}-tm-sync`;
};

const TeamSetup = ({ match, updateState, currentState, intl }) => {
  const [isReadyToShow, setIsReadyToShow] = useState(false);
  const [teamsData, setTeamsData] = useState([]);
  const [validationInfo, setValidationInfo] = useState({});
  const [overrideDefaultRoster, setOverrideDefaultRoster] = useState(false);
  const { formatMessage } = intl;
  const {
    competitors,
    errorMsg,
    hasLoaded,
    fixtureProfile,
    organization,
    forceRefresh,
    sport,
    season: fixtureSeason,
    status: fixtureStatus,
  } = useContext(FixtureDetailsContext);

  useEffect(() => {
    const storedTeamsData = localStorage.getItem(match.params.fixtureId + "-teams");
    if (storedTeamsData) {
      setTeamsData(JSON.parse(storedTeamsData));
    }
  }, [match.params.fixtureId]);

  const isFixtureConfirmed = () => {
    return fixtureStatus === "CONFIRMED";
  };

  const buildNewTeamsData = (overrideDefaultRoster = false) => {
    if (competitors) {
      const locallyStoredTeams = JSON.parse(localStorage.getItem(match.params.fixtureId + "-teams"));
      const isUsingFixtureRoster =
        (!overrideDefaultRoster && fixtureSeason?.defaultRoster === "fixture") ||
        (overrideDefaultRoster && fixtureSeason?.defaultRoster === "season");
      const newTeamsData = Array(competitors.length).fill(null);
      competitors.forEach((competitor) => {
        const team = {};
        const teamIndex = competitor.isHome ? 0 : 1;
        const savedEntityColor = localStorage.getItem(`${competitor.entityId}-color`);
        if (savedEntityColor !== null) {
          team.primaryColor = savedEntityColor;
        } else {
          team.primaryColor = teamIndex === 0 ? "#b71c1c" : "#0D47A1";
        }
        team.entityId = competitor.entityId;
        team.nameFullLocal = competitor.nameFullLocal;
        team.fullNameLatin = competitor.nameFullLocal;
        team.logo = competitor.logo;

        let storedTeam = null;
        if (currentState?.mqtt?.entities) {
          storedTeam = currentState.mqtt.entities.find((team) => competitor.entityId === team.entityId);
          if (storedTeam?.roles) {
            const newStaff = {};
            Object.entries(storedTeam.roles).forEach(([roleName, roleMembers]) => {
              Object.entries(roleMembers).forEach(([staffPersonIn, staffPersonData]) => {
                newStaff[staffPersonIn] = {
                  ...staffPersonData,
                  personId: staffPersonIn,
                  role: roleName,
                };
              });
            });
            storedTeam.staff = newStaff;
          }
        }

        let userStoredTeam = null;
        if (locallyStoredTeams) {
          userStoredTeam = locallyStoredTeams.find((t) => t.entityId === competitor.entityId);
        }

        let rosters = [];
        if (isUsingFixtureRoster) {
          rosters = competitor.rosters?.fixture ?? [];
        } else {
          rosters = competitor.rosters?.season ?? [];
        }

        if (
          !isUsingFixtureRoster &&
          !!fixtureSeason?.rosterConfiguration?.seasonRosterStatusAllowed &&
          fixtureSeason.rosterConfiguration.seasonRosterStatusAllowed.length > 0
        ) {
          rosters = rosters.filter((person) =>
            fixtureSeason.rosterConfiguration.seasonRosterStatusAllowed.includes(person.status),
          );
        }

        const allActiveBibs =
          storedTeam?.persons &&
          Object.keys(storedTeam.persons)
            // filter out persons that are removed from the roster
            .filter((personId) => rosters.some((rosterPerson) => rosterPerson.personId === personId))
            .map((personId) => storedTeam.persons[personId])
            .map((person) => person.bib);

        const activeBibSet = new Set(allActiveBibs);
        const duplicateBibs = allActiveBibs
          ? allActiveBibs.filter((item) => {
              if (activeBibSet.has(item)) {
                activeBibSet.delete(item);
                return undefined;
              } else {
                return item;
              }
            })
          : [];

        team.persons = [];
        rosters.forEach((personSrc) => {
          const storedPerson = storedTeam?.persons?.[personSrc.personId];
          const userStoredPerson = userStoredTeam?.persons.find((p) => p.personId === personSrc.personId);
          const person = storedPerson ?? userStoredPerson ?? personSrc;
          const isDuplicate = duplicateBibs.indexOf(person.bib) > -1;
          const isPlaying = person.playing ?? ((!storedPerson && person.starter) || !!storedPerson);
          const isStarter = person.starter ?? (isPlaying && !!person.starter);

          const getDesiredPlayingState = () => {
            if (userStoredPerson?.desiredPlayingState === undefined) {
              return undefined;
            }

            // still waiting for the valid persons message
            if (userStoredPerson?.desiredPlayingState !== isPlaying) {
              return userStoredPerson.desiredPlayingState;
            }

            // update happened successfully, clearing state
            if (userStoredPerson?.desiredPlayingState === isPlaying) {
              return undefined;
            }
          };

          team.persons.push({
            personId: personSrc.personId,
            bib: person.bib || "",
            nameFullLocal: person.nameFullLocal || person.name,
            position: person.position,
            starter: isStarter,
            playing: isPlaying,
            captain: !!person.captain,
            confirmed: !!storedPerson,
            duplicate: isDuplicate && isPlaying,
            desiredPlayingState: getDesiredPlayingState(),
          });
        });

        team.staff = uniqueBy(
          competitor.roles.season.map((personSrc) => {
            const storedPerson = storedTeam?.staff?.[personSrc.personId];
            const userStoredPerson = userStoredTeam?.staff.find((p) => p.personId === personSrc.personId);
            const person = storedPerson ?? userStoredPerson ?? personSrc;

            return {
              personId: person.personId,
              name: person.nameFullLocal || person.name,
              role: person.role,
              active: !!storedPerson,
              number: person.number,
              confirmed: !!storedPerson,
            };
          }),
          "personId",
        );

        team.staffState = {};
        // if there's a period that means this fixture has been started and the staff has been confirmed
        // no need to populate with some defaults
        const hasMatchStarted = !!currentState.period || teamsData.length > 0;
        // if there are any active staff members then we don't fill up all staff slots because
        // there were already staff members selected
        const anyActiveStaffMembers =
          team.staff.some((person) => person.active) || teamsData[0]?.staff.some((person) => person.active);

        if (userStoredTeam?.staffState) {
          team.staffState = { ...userStoredTeam?.staffState };
        } else {
          const getRoleActiveOrFirst = (role) => {
            const persons = team.staff.filter((person) => person.role === role);
            return persons.find((person) => person.active) ?? (hasMatchStarted || anyActiveStaffMembers ? false : persons[0])
          };

          const coachPerson = getRoleActiveOrFirst(StaffRoles.coach.role);
          const assistantCoaches = team.staff.filter((person) => person.role === StaffRoles.assistantCoach1.role);
          const activeAssistantCoaches = assistantCoaches.filter((person) => person.active);
          const activeAssistantCoach1 = activeAssistantCoaches.find((person) => person.number === 1);
          const assistantCoach1 = activeAssistantCoach1 ?? (hasMatchStarted || anyActiveStaffMembers ? false : assistantCoaches[0]);
          const activeAssistantCoach2 = activeAssistantCoaches.find((person) => person.number === 2);
          const assistantCoach2 = activeAssistantCoach2 ?? (hasMatchStarted || anyActiveStaffMembers ? false : assistantCoaches[1]);
          const managerPerson = getRoleActiveOrFirst(StaffRoles.manager.role);
          const physiotherapistPerson = getRoleActiveOrFirst(StaffRoles.physiotherapist.role);
          const doctorPerson = getRoleActiveOrFirst(StaffRoles.doctor.role);
          const officialAPerson = getRoleActiveOrFirst(StaffRoles.official_a.role);
          const officialBPerson = getRoleActiveOrFirst(StaffRoles.official_b.role);
          const officialCPerson = getRoleActiveOrFirst(StaffRoles.official_c.role);
          const officialDPerson = getRoleActiveOrFirst(StaffRoles.official_d.role);
          const officialEPerson = getRoleActiveOrFirst(StaffRoles.official_e.role);
          const others = team.staff.filter((person) => StaffRoles.other.roles.includes(person.role));
          const activeOther = others.find((person) => person.active);
          const otherPerson = activeOther ?? (hasMatchStarted || anyActiveStaffMembers ? false : others[0]);

          if (sport === "basketball") {
            team.staffState = {
              coach: coachPerson
                ? {
                    personId: coachPerson.personId,
                    active: coachPerson.active,
                  }
                : { personId: "", active: false },
              assistantCoach1: assistantCoach1
                ? {
                    personId: assistantCoach1.personId,
                    active: assistantCoach1.active,
                  }
                : { personId: "", active: false },
              assistantCoach2: assistantCoach2
                ? {
                    personId: assistantCoach2.personId,
                    active: assistantCoach2.active,
                  }
                : { personId: "", active: false },
            };
          } else if (sport === "hockey") {
            team.staffState = {
              coach: coachPerson
                ? {
                    personId: coachPerson.personId,
                    active: coachPerson.active,
                  }
                : { personId: "", active: false },
              manager: managerPerson
                ? {
                    personId: managerPerson.personId,
                    active: managerPerson.active,
                  }
                : { personId: "", active: false },
              physiotherapist: physiotherapistPerson
                ? {
                    personId: physiotherapistPerson.personId,
                    active: physiotherapistPerson.active,
                  }
                : { personId: "", active: false },
              doctor: doctorPerson
                ? {
                    personId: doctorPerson.personId,
                    active: doctorPerson.active,
                  }
                : { personId: "", active: false },
              other: otherPerson
                ? {
                    personId: otherPerson.personId,
                    active: otherPerson.active,
                  }
                : { personId: "", active: false },
            };
          } else if (sport === "handball") {
            team.staffState = {
              official_a: officialAPerson
                ? {
                    personId: officialAPerson.personId,
                    active: officialAPerson.active,
                  }
                : { personId: "", active: false },
              official_b: officialBPerson
                ? {
                    personId: officialBPerson.personId,
                    active: officialBPerson.active,
                  }
                : { personId: "", active: false },
              official_c: officialCPerson
                ? {
                    personId: officialCPerson.personId,
                    active: officialCPerson.active,
                  }
                : { personId: "", active: false },
              official_d: officialDPerson
                ? {
                    personId: officialDPerson.personId,
                    active: officialDPerson.active,
                  }
                : { personId: "", active: false },
              official_e: officialEPerson
                ? {
                    personId: officialEPerson.personId,
                    active: officialEPerson.active,
                  }
                : { personId: "", active: false },
            };
          } else {
            team.staffState = {
              coach: coachPerson
                ? {
                    personId: coachPerson.personId,
                    active: coachPerson.active,
                  }
                : { personId: "", active: false },
            };
          }
        }

        newTeamsData[teamIndex] = team;
      });
      return newTeamsData;
    }
    return [];
  };

  const updateTeam = useCallback(
    (newTeamsData) => {
      setTeamsData((oldTeams) =>
        oldTeams.map((team) => (team.entityId === newTeamsData.entityId ? newTeamsData : team)),
      );
      let tempEntities = currentState.entities;
      if (tempEntities) {
        let entIndex = tempEntities.findIndex((ent) => ent.entityId === newTeamsData.entityId);
        if (entIndex > -1) {
          tempEntities[entIndex].primaryColor = newTeamsData.primaryColor;
        }
      }
    },
    [currentState.entities],
  );

  useEffect(() => {
    const newValidation = {};
    teamsData.forEach((team) => {
      newValidation[team.entityId] = getValidationObject(team, fixtureProfile, sport);
    });
    setValidationInfo(newValidation);

    if (teamsData.length > 0) {
      localStorage.setItem(match.params.fixtureId + "-teams", JSON.stringify(teamsData));
    }
  }, [teamsData, match, fixtureProfile, sport]);

  useEffect(() => {
    if (currentState.catchupsDone) {
      setTeamsData(buildNewTeamsData(overrideDefaultRoster));
    }
    //TODO: fix once MQTT state can be properly tracked
    // eslint-disable-next-line
  }, [JSON.stringify(currentState.mqtt?.entities), currentState?.catchupsDone]);

  useEffect(() => {
    const isUsingFixtureRoster =
      (!overrideDefaultRoster && fixtureSeason?.defaultRoster === "fixture") ||
      (overrideDefaultRoster && fixtureSeason?.defaultRoster === "season");
    const hasMqttEntities = currentState.mqtt?.entities?.length > 0;
    if (currentState.mqtt && (hasMqttEntities || (isUsingFixtureRoster && teamsData?.length > 0))) {
      teamsData.forEach((team) => {
        const competitor = competitors.find((comp) => comp.entityId === team.entityId);
        const mqttEntity = currentState.mqtt?.entities?.find((entity) => entity.entityId === team.entityId);
        const mqttHasPersons = Object.keys(mqttEntity?.persons ?? {}).length > 0;

        if (!competitor.rosters.fixture?.length || isFixtureConfirmed() || mqttHasPersons) {
          return;
        }

        const storageKey = buildTeamManagerSyncStorageKey(match.params.fixtureId, team.entityId);

        if (window.localStorage.getItem(storageKey)) {
          return;
        }

        team.persons.forEach((person) => {
          if (hasMqttEntities || (isUsingFixtureRoster && person.playing)) {
            sendPlayer(person, team, currentState.mqtt, match.params.fixtureId, "added", sport);
          }
        });

        window.localStorage.setItem(storageKey, true);
      });
    }
  }, [teamsData, currentState.mqtt?.entities, overrideDefaultRoster, fixtureSeason?.defaultRoster]);

  useEffect(() => {
    const organizationId = organization?.organizationId;
    if (organizationId) {
      updateState("orgnizationId", organizationId);
      sessionStorage.setItem("orgId", organizationId);
    }
    if (competitors && currentState.catchupsDone) {
      setTeamsData(buildNewTeamsData(overrideDefaultRoster));
      setIsReadyToShow(true);
    }
    //TODO: updateState triggers constant re-renders, remove eslint disable once that's handled
    // eslint-disable-next-line
  }, [competitors, overrideDefaultRoster, currentState.catchupsDone]);

  const teamsDisplay = useMemo(() => {
    return teamsData.map((competitor, index) => {
      return <Team key={`team_info_${index}`} data={competitor} updateTeam={updateTeam} currentState={currentState} />;
    });
  }, [teamsData, currentState, updateTeam]);

  const titles = useMemo(() => {
    const participants = teamsData.filter((team) => !!team.nameFullLocal);
    const participantsTitle = participants.map((it) => getTruncatedTeamName(it.nameFullLocal, 32)).join(" v. ");
    const mainTitleBase = formatMessage({
      id: "setup",
      defaultMessage: "Team Setup",
    });
    const page = [participantsTitle, mainTitleBase].filter((e) => !!e).join(" - ");
    const document = `${page} - ${APP_TITLE}`;
    return { document, page };
  }, [teamsData, formatMessage]);

  const checkNoActivePlayers = () => {
    let playerSelected = false;
    teamsData.forEach((team) => {
      playerSelected = playerSelected || team.persons.some((person) => person.playing);
    });
    return !playerSelected;
  };

  const handleRosterSwitch = () => {
    if (checkNoActivePlayers()) {
      setOverrideDefaultRoster(!overrideDefaultRoster);
      setTeamsData(buildNewTeamsData(!overrideDefaultRoster));
    }
  };

  return (
    <PageDisplay
      documentTitle={titles.document}
      pageTitle={titles.page}
      error={errorMsg}
      isLoading={!hasLoaded}
      reload={forceRefresh}
      customHeaderButton={{
        handler: handleRosterSwitch,
        className: `roster-switch-button ${fixtureSeason?.allowRosterSelect ?? true ? "" : "hidden"} ${
          checkNoActivePlayers() ? "" : "disabled"
        }`,
        content:
          (!overrideDefaultRoster && fixtureSeason?.defaultRoster === "season") ||
          (overrideDefaultRoster && fixtureSeason?.defaultRoster === "fixture") ? (
            <span>
              {formatMessage({
                id: "roster.fetch.fixture",
                defaultMessage: "Fetch fixture roster",
              })}
            </span>
          ) : (
            <span>
              {formatMessage({
                id: "roster.fetch.season",
                defaultMessage: "Fetch season roster",
              })}
            </span>
          ),
      }}
    >
      {!isReadyToShow && (
        <div className="loading-container">
          <Spinner size="md" color="orange" />
        </div>
      )}
      {isReadyToShow && !errorMsg && (
        <>
          <div className="teams-container">{teamsDisplay}</div>
          <TeamSetupControls validationInfo={validationInfo} />
        </>
      )}
    </PageDisplay>
  );
};

export default withRouter(injectIntl(TeamSetup));
